"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metaInfo = [{
  label: "RESPONSE",
  value: "WITHIN 24 HOURS"
}, {
  label: "QUALITY",
  value: "LAB TESTED"
}, {
  label: "STORAGE",
  value: "COLD CHAIN"
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-primary/40 bg-primary/5 p-8">
        <p className="font-default text-xl text-foreground italic mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="h-12 px-6 text-xs tracking-[0.2em] uppercase border-border bg-transparent text-foreground hover:bg-foreground/5">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-primary font-default font-medium mb-2 data-[empty=true]:opacity-0 transition-opacity">
              Full Name
            </FormLabel>
            <FormControl>
              <Input {...field} type="text" placeholder="Enter your full name" className="h-16 px-0 bg-transparent border-0 border-b border-border text-lg font-default italic placeholder:text-muted-foreground placeholder:font-default placeholder:italic focus-visible:border-b-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-4px_var(--primary)] transition-all" />
            </FormControl>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-primary font-default font-medium mb-2">
              Email Address
            </FormLabel>
            <FormControl>
              <Input {...field} type="email" placeholder="Enter your email" className="h-16 px-0 bg-transparent border-0 border-b border-border text-lg font-default italic placeholder:text-muted-foreground placeholder:font-default placeholder:italic focus-visible:border-b-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-4px_var(--primary)] transition-all" />
            </FormControl>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-primary font-default font-medium mb-2">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input {...field} type="tel" placeholder="Enter your phone number" className="h-16 px-0 bg-transparent border-0 border-b border-border text-lg font-default italic placeholder:text-muted-foreground placeholder:font-default placeholder:italic focus-visible:border-b-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-4px_var(--primary)] transition-all" />
            </FormControl>
            <p className="mt-2 text-[11px] tracking-[0.15em] uppercase text-muted-foreground">
              We will contact you to verify your research credentials
            </p>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-primary font-default font-medium mb-2">
              Inquiry Date
            </FormLabel>
            <FormControl>
              <Input {...field} type="date" className="h-16 px-0 bg-transparent border-0 border-b border-border text-lg font-default italic text-foreground focus-visible:border-b-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-4px_var(--primary)] transition-all [color-scheme:dark]" />
            </FormControl>
            <p className="mt-2 text-[11px] tracking-[0.15em] uppercase text-muted-foreground">
              Select your preferred contact date
            </p>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-primary font-default font-medium mb-2">
              Research Requirements
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Describe your research requirements" rows={3} className="px-0 py-4 bg-transparent border-0 border-b border-border text-lg font-default italic placeholder:text-muted-foreground placeholder:font-default placeholder:italic focus-visible:border-b-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-4px_var(--primary)] transition-all resize-none min-h-[80px]" />
            </FormControl>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-[60px] bg-primary text-primary-foreground hover:bg-primary/90 text-sm tracking-[0.25em] uppercase font-default font-semibold shadow-2xl hover:shadow-[0_20px_60px_-10px_var(--primary)] transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting
            </> : <>
              Submit Inquiry
              <ArrowRight className="ml-1 size-4" />
            </>}
        </Button>
        <p className="mt-6 font-default italic text-sm text-muted-foreground">
          Strict confidentiality maintained. All research inquiries are vetted.
        </p>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4679" id="direct-inquiry" className="bg-background text-foreground py-24 md:py-40 lg:py-[200px] px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] tracking-[0.3em] uppercase font-default font-medium mb-12 !text-[rgba(251,189,35,1)]">
              — 06 / Direct Inquiry
            </p>

            <h2 className="font-default font-bold text-foreground text-5xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-tight">
              Precision
              <br />
              <span className="font-default italic font-normal">inquiry.</span>
            </h2>

            <p className="mt-12 lg:mt-[60px] font-default text-lg md:text-xl text-foreground/70 leading-relaxed max-w-[380px]">
              Iron Habit Peptides provides elite-grade research compounds. All inquiries are handled with professional discretion.
            </p>

            <div className="mt-16 space-y-0">
              {metaInfo.map((item, i) => <div key={i} data-index={i} className="border-t border-border py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground font-default">
                    {item.label}
                  </span>
                  <span className="text-[11px] tracking-[0.25em] uppercase text-foreground font-default font-medium">
                    {item.value}
                  </span>
                </div>)}
              <div className="border-t border-border" />
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="direct_inquiry_form_support_center" formId="direct_inquiry_form_support_center" sectionName="direct-inquiry" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            preferred_date: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            message: {
              required: false
            }
          }} submitText="Submit Inquiry" successMessage="Your inquiry has been received. We will respond within 24 hours." errorMessage="Unable to submit your inquiry. Please try again or contact us directly.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}